import { useMemo, useEffect, useState } from 'react';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { useLocalStorage } from '@rehooks/local-storage';
import useCheckInMutation from './hooks/useCheckInMutation';
import useEventSettingsData from './hooks/useEventSettingsData';
import useCallErrorIfNoGahOrAccessTokenPresent from './hooks/useCallErrorIfNoGahOrAccessTokenPresent';
import useSetGuestAccessHashFromUrlToLocalStorage from './hooks/useSetGuestAccessHashFromUrlToLocalStorage';

const bootstrap = getBootstrap();
const eventId = parseInt(bootstrap.get('event.id', null), 10);

/**
 * This hook will take care of all the necessary steps to retrieve:
 * - The event settings data from the API.
 * - The access token from the API.
 * - The check in token from the API.
 * - The guest access hash from the URL.
 * It will as well store the VEP tokens in local storage.
 *
 * @param {Object{ onError }} props - An object with onError callback that will be called if no GAH or Access token present or if authentication request fails.
 * @return {Object} eventSettings - The current eventSettings react-query request object from the API.
 */
export default function useVEPRequests({ onError } = {}) {
    const [vepTokens, setVepTokens] = useLocalStorage(`splashVirtualEvent/${eventId}`);
    // Set guest access hash if needed from URL.
    const guestAccessHashHasBeenSetOnLocalStorage = useSetGuestAccessHashFromUrlToLocalStorage({
        vepTokens,
        setVepTokens,
    });

    const [isCheckedIn, setIsCheckedIn] = useState(false);

    // Perform event settings request if requestProcessStep is set to "FETCH_EVENT_SETTINGS"
    const eventSettings = useEventSettingsData({
        vepTokens,
        enabled: guestAccessHashHasBeenSetOnLocalStorage,
        onError,
    });
    // Return the checkIn mutation function that will be used.
    const checkInMutation = useCheckInMutation({ vepTokens, setVepTokens, eventSettings: eventSettings?.data });
    const checkIn = () => {
        checkInMutation.mutate();
        setIsCheckedIn(true);
    };
    // Decide if we should perform the checkin request or not.
    const shouldFetchCheckInData = Boolean(vepTokens?.checkInToken && eventSettings?.data && !isCheckedIn);
    // Call error handler if no guest access hash or access token is present.
    useCallErrorIfNoGahOrAccessTokenPresent({ onError, vepTokens });

    // Check in is automatically done when the shouldFetchCheckInData is true.
    useEffect(() => {
        if (shouldFetchCheckInData) {
            checkInMutation.mutate();
        }
    }, [shouldFetchCheckInData]);

    return useMemo(
        () => ({
            virtualEventSettings: eventSettings?.data,
            actions: { checkIn },
        }),
        [eventSettings, checkInMutation],
    );
}
